import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, ChevronLeft, Search, Eye, Play, CheckCircle, Lock, FolderKanban, Calendar, Building2, TrendingUp, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { psAPI, companyAPI } from '../../../services/api';
import { toast } from 'sonner';

const ProjectList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ company_code_id: '', system_status: '' });
  const [companyCodes, setCompanyCodes] = useState([]);

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, ccRes] = await Promise.all([
        psAPI.getProjects(filters),
        companyAPI.getCompanyCodes()
      ]);
      setProjects(projectsRes.data);
      setCompanyCodes(ccRes.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async (projectId) => {
    try {
      await psAPI.releaseProject(projectId);
      toast.success('Project released successfully');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error(errorMsg.msg || JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || 'Failed to release');
      }
    }
  };

  const handleComplete = async (projectId) => {
    try {
      await psAPI.completeProject(projectId);
      toast.success('Project completed successfully');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error(errorMsg.msg || JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || 'Failed to complete');
      }
    }
  };

  const handleClose = async (projectId) => {
    try {
      await psAPI.closeProject(projectId);
      toast.success('Project closed successfully');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error(errorMsg.msg || JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || 'Failed to close');
      }
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'CREATED': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      'RELEASED': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'PARTIALLY_RELEASED': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      'TECO': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      'CLOSED': { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
      'LOCKED': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' }
    };
    const c = config[status] || config['CREATED'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status}
      </span>
    );
  };

  const filteredProjects = projects.filter(p =>
    p.project_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const stats = {
    total: projects.length,
    active: projects.filter(p => ['RELEASED', 'PARTIALLY_RELEASED'].includes(p.system_status)).length,
    completed: projects.filter(p => p.system_status === 'TECO').length,
    closed: projects.filter(p => p.system_status === 'CLOSED').length
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="projects-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ps')} className="hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-600 rounded-lg">
                  <FolderKanban className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Project Definitions</h1>
                  <p className="text-sm text-slate-500">Manage and monitor your project portfolio</p>
                </div>
              </div>
            </div>
          </div>
          {canWrite('project_system') && (
          <Button onClick={() => navigate('/ps/projects/create')} data-testid="create-project-btn" className="bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Projects</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <BarChart3 className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Active</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.active}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-600">Completed</p>
                  <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Closed</p>
                  <p className="text-2xl font-bold text-gray-700">{stats.closed}</p>
                </div>
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search by project ID or name..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-10 bg-slate-50 border-slate-200 focus:bg-white" 
                />
              </div>
              <Select value={filters.company_code_id || "all"} onValueChange={(v) => setFilters({...filters, company_code_id: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Company Code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companyCodes.map(cc => <SelectItem key={cc.company_code_id} value={cc.company_code_id}>{cc.company_code_id}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.system_status || "all"} onValueChange={(v) => setFilters({...filters, system_status: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="RELEASED">Released</SelectItem>
                  <SelectItem value="TECO">Completed</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Project ID</TableHead>
                  <TableHead className="font-semibold text-slate-600">Project Name</TableHead>
                  <TableHead className="font-semibold text-slate-600">Company</TableHead>
                  <TableHead className="font-semibold text-slate-600">Duration</TableHead>
                  <TableHead className="font-semibold text-slate-600">Status</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-center">WBS</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>
                        Loading projects...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <FolderKanban className="h-10 w-10 text-slate-300" />
                        <p className="text-slate-500">No projects found</p>
                        {canWrite('project_system') && (
                        <Button variant="outline" size="sm" onClick={() => navigate('/ps/projects/create')}>
                          Create your first project
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map((project) => (
                    <TableRow 
                      key={project.project_id} 
                      className="group hover:bg-orange-50/50 transition-colors cursor-pointer" 
                      data-testid={`project-row-${project.project_id}`}
                      onClick={() => navigate(`/ps/projects/${project.project_id}`)}
                    >
                      <TableCell>
                        <span className="font-mono text-sm font-medium text-orange-600">{project.project_id}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{project.name}</p>
                          {project.description && (
                            <p className="text-xs text-slate-500 truncate max-w-[250px]">{project.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{project.company_code_id}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span>{new Date(project.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          <span className="text-slate-400">→</span>
                          <span>{new Date(project.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(project.system_status)}</TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-sm font-medium text-slate-600">
                          {project.wbs_element_count || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200"
                            onClick={() => navigate(`/ps/projects/${project.project_id}`)} 
                            title="View Details"
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </Button>
                          {project.system_status === 'CREATED' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-orange-100"
                              onClick={() => handleRelease(project.project_id)} 
                              title="Release Project"
                            >
                              <Play className="h-4 w-4 text-orange-600" />
                            </Button>
                          )}
                          {project.system_status === 'RELEASED' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-emerald-100"
                              onClick={() => handleComplete(project.project_id)} 
                              title="Mark Complete"
                            >
                              <CheckCircle className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )}
                          {project.system_status === 'TECO' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-gray-200"
                              onClick={() => handleClose(project.project_id)} 
                              title="Close Project"
                            >
                              <Lock className="h-4 w-4 text-gray-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredProjects.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-500">
                Showing <span className="font-medium text-slate-700">{filteredProjects.length}</span> of <span className="font-medium text-slate-700">{projects.length}</span> projects
              </p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default ProjectList;
